import React, { useCallback } from 'react';
import { Box, IconButton, ListItemButton, Typography } from '@mui/material';
import HandymanIcon from '@mui/icons-material/Handyman';
import { MoreHorizontal as MoreHorizontalIcon } from 'lucide-react';
import { LabourItemDefinition } from '../../api/labourItemsApi';

interface LabourItemListItemProps {
  item: LabourItemDefinition;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, id: string) => void;
  disabled?: boolean;
}

export const LabourItemListItem = React.memo(function LabourItemListItem({
  item,
  onMenuOpen,
  disabled,
}: LabourItemListItemProps) {
  const handleMenuOpen = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      onMenuOpen(e, item.id);
    },
    [onMenuOpen, item.id]
  );

  return (
    <ListItemButton
      component="div"
      disableRipple
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: { xs: 2, sm: 3 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: 'default',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <HandymanIcon
        sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }}
      />

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.name}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {item.groupCount === 0
            ? 'No groups'
            : `${item.groupCount} group${item.groupCount !== 1 ? 's' : ''}`}
        </Typography>
      </Box>

      <IconButton
        size="small"
        onClick={handleMenuOpen}
        disabled={disabled}
        data-testid="labour-item-card-menu"
        sx={{ flexShrink: 0 }}
      >
        <MoreHorizontalIcon size={18} />
      </IconButton>
    </ListItemButton>
  );
});
